/**
 * /customer/claim-stamp/history — Customer claim history (Phase R3).
 *
 * Mirrors mobile `(customer)/claim-stamp/history.tsx`. Simple list
 * of the customer's past receipt claims with brand name, submission
 * date, extracted total, and a status pill. Tap forwards to the
 * status screen for that claim.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeft, ChevronRight, Receipt, Clock, CheckCircle2, XCircle,
  Hourglass, Loader2,
} from 'lucide-react';

import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { formatRupiah, claimStatusLabel } from '@/lib/claimErrorUI';

export default function CustomerClaimStampHistoryPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);

  const claimsQ = useQuery({
    queryKey: qk.customerReceiptClaims({}),
    queryFn: () => api.customerListReceiptClaims(token),
    enabled: !!token,
    refetchOnWindowFocus: true,
  });

  const brandsQ = useQuery({
    queryKey: qk.customerBrands(),
    queryFn: () => api.customerBrands(token),
    enabled: !!token,
    staleTime: 60_000,
  });

  const brandMap = useMemo(() => {
    const map = {};
    for (const b of brandsQ.data ?? []) map[b.id] = b;
    return map;
  }, [brandsQ.data]);

  const claims = claimsQ.data ?? [];

  return (
    <div className="relative" data-testid="claim-history">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-gold/12 to-transparent -z-10" aria-hidden="true" />

      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-white/55 hover:text-white"
        data-testid="claim-history-back"
      >
        <ChevronLeft size={14} /> Back
      </button>

      <div className="mt-4">
        <div className="text-xs uppercase tracking-widest text-gold font-semibold">
          History
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1.5">
          My Receipt Claims
        </h1>
        <p className="text-sm text-white/60 mt-2 max-w-xl">
          Review the status of every receipt you&apos;ve submitted.
        </p>
      </div>

      {claimsQ.isLoading ? (
        <div className="mt-10 flex justify-center">
          <Loader2 size={28} className="text-gold animate-spin" />
        </div>
      ) : claims.length === 0 ? (
        <div
          className="mt-10 border border-dashed border-white/10 rounded-3xl bg-[#15151B]/60 p-12 text-center max-w-xl mx-auto"
          data-testid="claim-history-empty"
        >
          <div className="inline-flex w-20 h-20 rounded-full border border-gold/30 bg-gold/10 items-center justify-center mb-5">
            <Receipt size={28} className="text-gold" />
          </div>
          <h3 className="font-display text-xl font-semibold">No claims yet</h3>
          <p className="text-sm text-white/55 mt-2 max-w-sm mx-auto">
            Open a brand card and tap &ldquo;Claim Stamp from Receipt&rdquo;
            to submit your first receipt.
          </p>
          <button
            type="button"
            onClick={() => navigate('/customer/brands')}
            data-testid="claim-history-empty-cta"
            className="mt-6 inline-flex items-center justify-center gap-2 bg-transparent border border-gold/40 text-gold font-semibold rounded-2xl px-5 py-2.5 text-sm hover:bg-gold/10 transition-colors min-h-[44px]"
          >
            Browse brands
          </button>
        </div>
      ) : (
        <div className="mt-6 space-y-3 max-w-2xl">
          {claims.map((c) => {
            const brandName =
              brandMap[c.brand_id]?.name ??
              c.ai_extraction?.brand_name ??
              'Unknown brand';
            const submitted = new Date(c.created_at).toLocaleDateString('id-ID', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            });
            const total =
              c.ai_extraction?.total_amount != null
                ? ` · ${formatRupiah(c.ai_extraction.total_amount)}`
                : '';
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => navigate(`/customer/claim-stamp/status/${c.id}`)}
                data-testid={`claim-history-row-${c.id}`}
                className="w-full flex items-center gap-3 rounded-2xl border border-white/8 bg-[#15151B] p-4 text-left hover:border-gold/35 hover:bg-[#1A1A20] transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-white truncate">
                    {brandName}
                  </div>
                  <div className="text-xs text-white/55 mt-0.5 truncate">
                    {submitted}{total}
                  </div>
                </div>
                <StatusPill status={c.status} />
                <ChevronRight size={16} className="text-white/30 shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending_ai_analysis:  { icon: Hourglass,     cls: 'bg-gold/15 text-gold border-gold/35' },
    pending_owner_review: { icon: Clock,         cls: 'bg-gold/15 text-gold border-gold/35' },
    approved:             { icon: CheckCircle2,  cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/35' },
    rejected:             { icon: XCircle,       cls: 'bg-red-500/15 text-red-300 border-red-500/35' },
  };
  const m = map[status] ?? map.pending_owner_review;
  const Icon = m.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold whitespace-nowrap ${m.cls}`}
      data-testid={`claim-history-status-${status}`}
    >
      <Icon size={12} />
      {claimStatusLabel(status)}
    </span>
  );
}
